import React, { useEffect, useMemo, useState } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseForm } from "@app/components/common/forms/BaseForm/BaseForm";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { BasePopconfirm } from "@app/components/common/BasePopconfirm/BasePopconfirm";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import {
  message,
  Modal,
  Input,
  Select,
  Form,
  Row,
  Col,
  Drawer,
  Tabs,
  Switch,
  Radio,
  Alert,
  Space,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  adminApi,
  AttendanceRecordCreateData,
  Employee,
  EmployeeDetail,
  CompanyApplication,
} from "@app/api/admin";
import dayjs from "dayjs";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import {
  initialPagination,
  initialPaginationProps,
  useCompanyList,
} from "../CompanyPage";
import { useWatch } from "antd/lib/form/Form";
import { useDepartment } from "../DepartmentPage";
import { useUserList } from "../UserPage";
import EmployeeBasicInfo from "./components/EmployeeBasicInfo";
import BankAccountManagement from "./components/BankAccountManagement";
import ContractManagement from "./components/ContractManagement";
import RoleManagement from "./components/RoleManagement";
import HolidayBalanceManagement from "./components/HolidayBalanceManagement";
import { BaseUpload } from "@app/components/common/BaseUpload/BaseUpload";
import OSSImageUpload from "@app/components/common/OSSImageUpload/OSSImageUpload";
import { BaseDatePicker } from "@app/components/common/pickers/BaseDatePicker";
import { useApplicationUser } from "../CompanyApplicationPage";

export enum EmployeeStatusEnum {
  OnJob = "在职",
  OffJob = "离职",
  Trial = "试用期",
}

const EmployeePage: React.FC = () => {
  // 将工龄格式化为保留一位小数的“X.X年”形式
  const formatSeniority = (years?: number) => {
    if (years === null || years === undefined || Number.isNaN(Number(years))) {
      return "-";
    }
    const val = Math.round(Number(years) * 10) / 10;
    return `${val}年`;
  };
  const [createForm] = BaseForm.useForm();
  const [searchForm] = Form.useForm();

  const [tableData, setTableData] = useState<{
    data: Employee[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [loading, setLoading] = useState(false);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // 添加查看员工信息的状态
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewEmployeeData, setViewEmployeeData] =
    useState<AttendanceRecordCreateData>();
  const [viewLoading, setViewLoading] = useState(false);

  // const [companyNameVal, setCompanyNameVal] = useState<string>("");

  // 员工个人信息
  const [viewEmployeeInfo, setViewEmployeeInfo] = useState<EmployeeDetail>();

  // 移除单独的搜索状态，改用统一的 searchParams
  const [searchParams, setSearchParams] = useState<{
    keyword?: string;
    status?: number;
    department_id?: number;
  }>({});

  // 添加独立的分页状态
  const [pagination, setPagination] = useState(initialPagination);

  const [isEdit, setIsEdit] = useState(false);

  const parseBirthdayFromIdCard = (id: string) => {
    const v = (id || "").trim();
    if (/^\d{17}[\dXx]$/.test(v)) {
      const y = v.slice(6, 10);
      const m = v.slice(10, 12);
      const d = v.slice(12, 14);
      const date = dayjs(`${y}-${m}-${d}`, "YYYY-MM-DD", true);
      return date.isValid() ? date : null;
    }
    if (/^\d{15}$/.test(v)) {
      const y = `19${v.slice(6, 8)}`;
      const m = v.slice(8, 10);
      const d = v.slice(10, 12);
      const date = dayjs(`${y}-${m}-${d}`, "YYYY-MM-DD", true);
      return date.isValid() ? date : null;
    }
    return null;
  };

  const handleIdCardBlur = (value: string) => {
    const date = parseBirthdayFromIdCard(value);
    console.log(date);
    if (date) {
      createForm.setFieldsValue({ birthday: date });
    }
  };

  // 获取员工列表数据
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const { total, ...rest } = pagination;
      const { data } = await adminApi.employees.list({
        ...rest,
        ...searchParams,
      });

      // 直接使用接口返回的数据
      setTableData({
        data: data.employees || [],
        pagination: data.pagination,
      });
    } catch (error: any) {
      console.error(error.message || "获取员工列表失败");
    } finally {
      setLoading(false);
    }
  };

  const { departmentList } = useDepartment();

  const { userList } = useApplicationUser();

  // 添加 useEffect 监听搜索参数和分页变化
  useUpdateEffect(() => {
    fetchEmployees();
  }, [searchParams, pagination]);

  // 搜索处理 - 更新为表单提交方式
  const handleSearch = (values: any) => {
    setSearchParams(values);
    setPagination({ ...initialPagination, page: 1 });
  };

  // 重置搜索 - 更新为表单重置方式
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPagination({ ...initialPagination, page: 1 });
  };

  // 查看员工信息
  const handleViewEmployee = async (record: Employee) => {
    try {
      setViewLoading(true);
      const { data } = await adminApi.employees.getRoleCompany(record.id);
      const { data: holidayBalance } =
        await adminApi.employees.getHolidayBalance(record.id);
      // 员工个人信息
      const { data: employeeInfo } = await adminApi.employees.detail(record.id);
      setViewEmployeeInfo(employeeInfo);
      setViewEmployeeData(data);
      setIsViewModalVisible(true);
    } catch (error: any) {
      message.error(error.message || "获取员工信息失败");
    } finally {
      setViewLoading(false);
    }
  };

  // 编辑功能
  const handleEdit = async (record: Employee) => {
    console.log("编辑员工数据:", record);
    const company = userList.find((item) => item.user_id === record.user_id);
    createForm.setFieldsValue({
      ...record,
      user_id: userList.find((item) => item.user_id === record.user_id)?.id,
      department_id: record.department?.id,
      company_name: record.company?.name || company?.company_name || "",
      company_id: record.company?.id || company?.company_id || 0,
      hire_date: record.hire_date
        ? dayjs(record.hire_date, "YYYY-MM-DD")
        : null,
      birthday: record.birthday ? dayjs(record.birthday, "YYYY-MM-DD") : null,

      // 直接使用独立的身份证正反面字段
      id_card_front_url: record.id_card_front_url
        ? [record.id_card_front_url]
        : [],
      id_card_back_url: record.id_card_back_url
        ? [record.id_card_back_url]
        : [],
      driver_license_front_url: record.driver_license_front_url
        ? [record.driver_license_front_url]
        : [],
      driver_license_back_url: record.driver_license_back_url
        ? [record.driver_license_back_url]
        : [],
    });
    setIsEdit(true);
    setIsCreateModalVisible(true);
  };

  // 分页变化处理 - 更新为直接设置分页状态
  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      page_size: pageSize,
    });
  };

  // 删除
  const handleDeleteRow = async (id: number) => {
    try {
      await adminApi.employees.delete(id);
      fetchEmployees();
      message.success("删除成功");
    } catch (error: any) {
      message.error(error.message || "删除失败");
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "员工姓名",
        dataIndex: "name",
        width: 180,
      },
      {
        title: "手机号码",
        dataIndex: "phone_number",
        width: 144,
      },
      // 公司ID
      {
        title: "公司",
        dataIndex: "company",
        width: 144,
        render: (company: any) => company?.name || "-",
      },
      {
        title: "部门",
        dataIndex: "department",
        width: 144,
        render: (department: any) => department?.name || "-",
      },
      {
        title: "性别",
        dataIndex: "gender",
        width: 96,
      },

      {
        title: "入职日期",
        dataIndex: "hire_date",
        width: 144,
        render: (date: string) =>
          date ? dayjs(date).format("YYYY-MM-DD") : "-",
      },
      {
        title: "学历",
        dataIndex: "education",
        width: 120,
      },
      {
        title: "政治面貌",
        dataIndex: "political_status",
        width: 120,
      },

      {
        title: "工龄",
        dataIndex: "seniority",
        width: 96,
        render: (seniority: number) => formatSeniority(seniority),
      },
      {
        title: "状态",
        dataIndex: "status",
        width: 96,
      },
      {
        title: "操作",
        dataIndex: "actions",
        width: 240,
        fixed: "right" as const,
        render: (_: any, record: Employee) => {
          return (
            <BaseSpace>
              <BaseButton
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => handleViewEmployee(record)}
                loading={viewLoading}
                size="small"
              >
                查看
              </BaseButton>
              <BaseButton
                type="ghost"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              >
                编辑
              </BaseButton>
              <BasePopconfirm
                title="确定要删除这个员工吗？"
                onConfirm={() => handleDeleteRow(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <BaseButton
                  icon={<DeleteOutlined />}
                  type="default"
                  size="small"
                  danger
                >
                  删除
                </BaseButton>
              </BasePopconfirm>
            </BaseSpace>
          );
        },
      },
    ],
    [userList]
  );

  // 新增员工
  const handleAddEmployee = () => {
    setIsCreateModalVisible(true);
    setIsEdit(false);
    createForm.resetFields();
  };

  // 提交表单
  const handleCreateSubmit = async () => {
    try {
      setCreateLoading(true);
      const values = await createForm.validateFields();
      console.log(values);
      // 处理日期格式
      const submitData = {
        ...values,
        user_id: userList.find((item) => item.id === values.user_id)?.user_id,
        hire_date: values.hire_date
          ? dayjs(values.hire_date).format("YYYY-MM-DD")
          : null,
        birthday: values.birthday
          ? dayjs(values.birthday).format("YYYY-MM-DD")
          : null,
        // 处理身份证照片字段 - 作为独立字段提交
        id_card_front_url: values?.id_card_front_url?.[0] || "",
        id_card_back_url: values?.id_card_back_url?.[0] || "",
        driver_license_front_url: values?.driver_license_front_url?.[0] || "",
        driver_license_back_url: values?.driver_license_back_url?.[0] || "",
        a: 111111,
      };
      console.log("submitData", submitData);

      isEdit
        ? await adminApi.employees.update(values.id, submitData)
        : await adminApi.employees.create(submitData);

      fetchEmployees();
      message.success(isEdit ? "编辑成功" : "创建成功");
      setIsCreateModalVisible(false);
      createForm.resetFields();
    } catch (error: any) {
      console.error("创建员工失败", error);
      if (error.errorFields?.length) {
        message.error(error.errorFields?.[0]?.errors?.[0]);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // 关闭弹窗
  const handleCreateCancel = () => {
    createForm.resetFields();
    setIsCreateModalVisible(false);
    setIsEdit(false);
  };

  const handleViewCancel = () => {
    setIsViewModalVisible(false);
  };

  const statusVal = useWatch("status", createForm);

  const salaryMethodVal = useWatch("salary_payment_method", createForm);
  const userIdVal = useWatch("user_id", createForm);
  const companyIdVal = useWatch("company_id", createForm);
  const searchCompanyIdVal = useWatch("company_id", searchForm);

  // 监听搜索表单中的公司变化，重置部门
  useEffect(() => {
    searchForm.setFieldValue("department_id", undefined);
  }, [searchCompanyIdVal, searchForm]);

  // 获取用户申请数据的函数
  const fetchApplicationData = async (id: number) => {
    try {
      const userId = userList.find((item) => item.id === id)?.user_id;
      if (!userId) {
        message.error("用户不存在");
        return;
      }
      const response = await adminApi.employees.getApplicationData(userId);
      const applicationData: CompanyApplication = response.data;
      // 映射申请数据到表单字段
      const formData = {
        name: applicationData.name,
        company_id: applicationData.company_id,
        company_name: applicationData.company_name,
        phone_number: applicationData.phone,
        position: applicationData.position,
        gender: applicationData.gender,
        id_card_number: applicationData.id_card_number,
        birthday_type: applicationData.birthday_type,
        birthday: applicationData.birthday
          ? dayjs(applicationData.birthday)
          : parseBirthdayFromIdCard(applicationData.id_card_number) || null,
        education: applicationData.education,
        political_status: applicationData.political_status,
        department_id: departmentList.find(
          (item) => item.id === applicationData.department_id
        )?.id,
        // 处理图片字段 - 转换为数组格式
        id_card_front_url: applicationData.id_card_front_url
          ? [applicationData.id_card_front_url]
          : [],
        id_card_back_url: applicationData.id_card_back_url
          ? [applicationData.id_card_back_url]
          : [],
        driver_license_front_url: applicationData.driver_license_front_url
          ? [applicationData.driver_license_front_url]
          : [],
        driver_license_back_url: applicationData.driver_license_back_url
          ? [applicationData.driver_license_back_url]
          : [],
      };

      // 只设置有值的字段
      Object.keys(formData).forEach((key) => {
        const value = formData[key as keyof typeof formData];
        if (value !== undefined && value !== null && value !== "") {
          createForm.setFieldValue(key, value);
        }
      });

      message.success("已自动填充申请信息");
    } catch (error) {
      console.error("获取申请数据失败:", error);
    }
  };

  // 监听用户ID变化，自动填充申请数据
  useEffect(() => {
    if (userIdVal && !isEdit) {
      fetchApplicationData(userIdVal);
    }
  }, [userIdVal, isEdit, departmentList]);

  const { companyList } = useCompanyList()

  return (
    <>
      <PageTitle>员工管理</PageTitle>
      <Alert
        type="info"
        message="页面功能说明"
        description={
          <>
            此页面展示已加入公司的员工，包括在职、离职和试用期员工，可进行员工新增、编辑、删除操作
          </>
        }
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 搜索表单 - 重构为 Form 组件 */}
      <Form
        form={searchForm}
        onFinish={handleSearch}
        layout="inline"
        style={{
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]} style={{ width: "100%" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="search" style={{ marginBottom: 0 }}>
              <Input placeholder="员工姓名/手机号" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              name="company_id"
            >
              <Select placeholder="请选择公司">
                {companyList.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="department_id" style={{ marginBottom: 0 }}>
              <Select
                placeholder="部门筛选"
                allowClear
                style={{ width: "100%" }}
              >
                {departmentList
                  .filter(
                    (dept) =>
                      !searchCompanyIdVal ||
                      dept.company?.id === searchCompanyIdVal
                  )
                  .map((dept) => (
                    <Select.Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="status" style={{ marginBottom: 0 }}>
              <Select
                placeholder="状态筛选"
                allowClear
                style={{ width: "100%" }}
              >
                <Select.Option value={EmployeeStatusEnum.OnJob}>
                  在职
                </Select.Option>
                <Select.Option value={EmployeeStatusEnum.OffJob}>
                  离职
                </Select.Option>
                <Select.Option value={EmployeeStatusEnum.Trial}>
                  试用期
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={8} lg={6}>
            <Form.Item style={{ marginBottom: 0 }}>
              <BaseSpace>
                <BaseButton
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </BaseButton>
                <BaseButton onClick={handleReset}>重置</BaseButton>
                <BaseButton type="primary" onClick={handleAddEmployee}>
                  新增员工
                </BaseButton>
              </BaseSpace>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        dataSource={tableData.data}
        columns={columns}
        pagination={{
          ...tableData.pagination,
          onChange: handleTableChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1800 }}
      />

      {/* 查看员工信息模态框 */}
      <Drawer
        title="员工详细信息"
        visible={isViewModalVisible}
        onClose={handleViewCancel}
        width={1000}
        destroyOnClose
      >
        <Tabs defaultActiveKey="basic">
          <Tabs.TabPane tab="基本信息" key="basic">
            <EmployeeBasicInfo
              viewEmployeeData={viewEmployeeData}
              viewEmployeeInfo={viewEmployeeInfo}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="银行账户" key="bankAccount">
            <BankAccountManagement
              employeeId={viewEmployeeData?.employee_id}
              employeeName={viewEmployeeData?.employee_name}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="合同管理" key="contract">
            <ContractManagement
              employeeId={viewEmployeeData?.employee_id}
              employeeName={viewEmployeeData?.employee_name}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="角色管理" key="role">
            <RoleManagement
              userId={viewEmployeeInfo?.user_id}
              employeeName={viewEmployeeData?.employee_name}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="假期余额" key="holidayBalance">
            <HolidayBalanceManagement
              employeeId={viewEmployeeData?.employee_id}
              employeeName={viewEmployeeData?.employee_name}
            />
          </Tabs.TabPane>
        </Tabs>
      </Drawer>

      {/* 创建/编辑员工模态框 */}
      <Modal
        title={isEdit ? "编辑员工" : "创建员工"}
        visible={isCreateModalVisible}
        onOk={handleCreateSubmit}
        onCancel={handleCreateCancel}
        confirmLoading={createLoading}
        width={800}
        destroyOnClose
      >
        <BaseForm
          form={createForm}
          layout="vertical"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
          initialValues={{
            id_card_front_url: [],
            id_card_back_url: [],
            driver_license_front_url: [],
            driver_license_back_url: [],
            participate_seniority_calculation: true,
            gender: '男',
            status: EmployeeStatusEnum.OnJob,
            hire_date: dayjs(),
          }}
        >
          <BaseForm.Item label="员工ID" name="id" hidden>
            <Input />
          </BaseForm.Item>

          {/* 关联用户 */}
          <BaseForm.Item
            label="关联用户"
            name="user_id"
            rules={[{ required: true, message: "请选择关联用户" }]}
          >
            <Select
              placeholder="请选择关联用户"
              showSearch
              optionFilterProp="children"
              disabled={isEdit}
            >
              {userList.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name}
                </Select.Option>
              ))}
            </Select>
          </BaseForm.Item>
          <BaseForm.Item label="公司名称" name="company_name">
            <Input disabled />
          </BaseForm.Item>
          <BaseForm.Item label="公司ID" name="company_id" hidden>
            <Input />
          </BaseForm.Item>
          <BaseForm.Item
            label="员工姓名"
            name="name"
            rules={[{ required: true, message: "请输入员工姓名" }]}
          >
            <Input placeholder="请输入员工姓名" maxLength={50} showCount />
          </BaseForm.Item>

          <BaseForm.Item
            label="性别"
            name="gender"
            rules={[{ required: true, message: "请选择性别" }]}
          >
            <Radio.Group>
              <Radio value="男">男</Radio>
              <Radio value="女">女</Radio>
            </Radio.Group>
          </BaseForm.Item>
          <BaseForm.Item
            label="身份证号"
            name="id_card_number"
            rules={[{ required: true, message: "请输入身份证号" }]}
          >
            <Input
              placeholder="请输入身份证号"
              maxLength={18}
              showCount
              onBlur={(e) => handleIdCardBlur(e.target.value)}
            />
          </BaseForm.Item>
          <Space>
            <BaseForm.Item
              label="身份证照片(正面)"
              name="id_card_front_url"
              rules={[{ required: false, message: "请上传身份证正面照片" }]}
            >
              <OSSImageUpload />
            </BaseForm.Item>
            <BaseForm.Item
              label="身份证照片(反面)"
              name="id_card_back_url"
              rules={[{ required: false, message: "请上传身份证反面照片" }]}
            >
              <OSSImageUpload />
            </BaseForm.Item>
            <BaseForm.Item
              label="驾驶证照片(正面)"
              name="driver_license_front_url"
              rules={[{ required: false, message: "请上传驾驶证照片(正面)" }]}
            >
              <OSSImageUpload />
            </BaseForm.Item>
            <BaseForm.Item
              label="驾驶证照片(反面)"
              name="driver_license_back_url"
              rules={[{ required: false, message: "请上传驾驶证照片(反面)" }]}
            >
              <OSSImageUpload />
            </BaseForm.Item>
          </Space>

          <BaseForm.Item
            label="学历"
            name="education"
            rules={[{ required: true, message: "请输入学历" }]}
          >
            <Select placeholder="请选择学历">
              <Select.Option value="小学">小学</Select.Option>
              <Select.Option value="初中">初中</Select.Option>
              <Select.Option value="高中">高中</Select.Option>
              <Select.Option value="中专">中专</Select.Option>
              <Select.Option value="大专">大专</Select.Option>
              <Select.Option value="本科">本科</Select.Option>
              <Select.Option value="硕士">硕士</Select.Option>
              <Select.Option value="博士">博士</Select.Option>
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="政治面貌"
            name="political_status"
            rules={[{ required: true, message: "请选择政治面貌" }]}
          >
            <Select placeholder="请选择政治面貌">
              <Select.Option value="群众">群众</Select.Option>
              <Select.Option value="团员">团员</Select.Option>
              <Select.Option value="党员">党员</Select.Option>
              <Select.Option value="民主党派">民主党派</Select.Option>
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="生日类型"
            name="birthday_type"
            rules={[{ required: true, message: "请选择生日类型" }]}
          >
            <Select placeholder="请选择生日类型">
              <Select.Option value="公历">公历</Select.Option>
              <Select.Option value="农历">农历</Select.Option>
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="生日"
            name="birthday"
            rules={[{ required: true, message: "请选择生日" }]}
          >
            <BaseDatePicker
              style={{ width: "100%" }}
              placeholder="请选择生日"
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="手机号码"
            name="phone_number"
            rules={[
              { required: true, message: "请输入手机号码" },
              { pattern: /^1[3-9]\d{9}$/, message: "请输入有效的手机号码" },
            ]}
          >
            <Input placeholder="请输入手机号码" maxLength={11} />
          </BaseForm.Item>

          {/* 紧急联系人信息 */}
          <BaseForm.Item
            label="紧急联系人"
            name="emergency_contact"
            rules={[{ max: 20, message: "紧急联系人不超过20个字符" }]}
          >
            <Input placeholder="请输入紧急联系人" maxLength={20} showCount />
          </BaseForm.Item>
          <BaseForm.Item
            label="紧急联系人手机号"
            name="emergency_contact_phone"
            rules={[
              { pattern: /^1[3-9]\d{9}$/, message: "请输入有效的手机号码" },
            ]}
          >
            <Input placeholder="请输入紧急联系人手机号" maxLength={11} />
          </BaseForm.Item>
          <BaseForm.Item
            label="所属部门"
            name="department_id"
            rules={[{ required: true, message: "请选择所属部门" }]}
          >
            <Select placeholder="请选择所属部门">
              {departmentList
                .filter((dept) => !companyIdVal || dept.company?.id === companyIdVal)
                .map((dept) => (
                  <Select.Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Select.Option>
                ))}
            </Select>
          </BaseForm.Item>
          <BaseForm.Item
            label="合同类型"
            name="contract_type"
            // rules={[{ required: true, message: "请选择合同类型" }]}
          >
            <Select placeholder="请选择合同类型">
              <Select.Option value="劳务合同">劳务合同</Select.Option>
              <Select.Option value="实习合同">实习合同</Select.Option>
              <Select.Option value="其他合同">其他合同</Select.Option>
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="入职日期"
            name="hire_date"
            rules={[{ required: true, message: "请选择入职日期" }]}
          >
            <BaseDatePicker
              style={{ width: "100%" }}
              placeholder="请选择入职日期"
            />
          </BaseForm.Item>
          <BaseForm.Item
            label="工资发放方式"
            name="salary_payment_method"
            // rules={[{ required: true, message: "请选择工资发放方式" }]}
          >
            <Select placeholder="请选择工资发放方式">
              <Select.Option value="银行转账">银行转账</Select.Option>
              <Select.Option value="现金发放">现金发放</Select.Option>
              <Select.Option value="支付宝">支付宝</Select.Option>
              <Select.Option value="微信">微信</Select.Option>
            </Select>
          </BaseForm.Item>
          {/* 如果选择的是银行转账 就显示开户行和银行卡号 */}
          {salaryMethodVal === "银行转账" && (
            <>
              <BaseForm.Item
                label="开户行"
                name="bank_name"
                rules={[{ required: true, message: "请输入开户行" }]}
              >
                <Input placeholder="请输入开户行" maxLength={50} showCount />
              </BaseForm.Item>
              <BaseForm.Item
                label="银行卡号"
                name="bank_card_number"
                rules={[{ required: true, message: "请输入银行卡号" }]}
              >
                <Input placeholder="请输入银行卡号" maxLength={50} showCount />
              </BaseForm.Item>
            </>
          )}

          <BaseForm.Item
            label="状态"
            name="status"
            rules={[{ required: true, message: "请选择状态" }]}
          >
            <Radio.Group>
              <Radio value={EmployeeStatusEnum.OnJob}>在职</Radio>
              <Radio value={EmployeeStatusEnum.OffJob}>离职</Radio>
              <Radio value={EmployeeStatusEnum.Trial}>试用期</Radio>
            </Radio.Group>
          </BaseForm.Item>
          {/* resignation_date 离职日期 如果status === 离职 就显示离职日期 */}
          {statusVal === EmployeeStatusEnum.OffJob && (
            <BaseForm.Item
              label="离职日期"
              name="resignation_date"
              rules={[{ required: true, message: "请选择离职日期" }]}
            >
              <BaseDatePicker
                style={{ width: "100%" }}
                placeholder="请选择离职日期"
              />
            </BaseForm.Item>
          )}
          {/* participate_seniority_calculation 是否参与工龄计算 */}
          <BaseForm.Item
            label="是否参与工龄计算"
            name="participate_seniority_calculation"
            rules={[{ required: true, message: "请选择是否参与工龄计算" }]}
            valuePropName="checked"
          >
            <Switch />
          </BaseForm.Item>
        </BaseForm>
      </Modal>
    </>
  );
};

export default EmployeePage;

export const useEmployeeList = () => {
  const [employeeList, setEmployeeList] = useState<Employee[]>([]);

  const fetchEmployeeList = async () => {
    try {
      const { data } = await adminApi.employees.list({
        page: 1,
        page_size: 100,
      });
      setEmployeeList(data.employees || []);
    } catch (error: any) {
      console.error(error);
    }
  };

  useUpdateEffect(() => {
    fetchEmployeeList();
  }, []);

  return {
    employeeList,
    fetchEmployeeList,
  };
};
